import { Navigate, useLocation, useParams } from 'react-router-dom';

type TabFallbackProps = {
    /** Path segment of the tab to land on, relative to the dashboard the tabs belong to. */
    to: string;
};

/**
 * Redirect an unknown tab path to a real one.
 *
 * Used as the `path="*"` element of a tabbed dashboard. A bare `<Navigate to="items" />` cannot
 * do this: under a splat route it resolves against the *unmatched* path, so `/service-plans/x/foo`
 * becomes `/service-plans/x/foo/items`, which is unknown too — and the redirect appends `items`
 * again on every pass, spinning until the URL is thousands of characters long.
 *
 * Trimming the splat off the current pathname yields the dashboard's own path, so the redirect
 * lands on a sibling of the unknown segment instead of a child of it.
 */
const TabFallback = ({ to }: TabFallbackProps) => {
    const { pathname } = useLocation();
    const splat = useParams()['*'] ?? '';

    const base = (splat ? pathname.slice(0, pathname.length - splat.length) : pathname).replace(/\/+$/, '');

    return <Navigate to={`${base}/${to}`} replace />;
};

export default TabFallback;
